import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Loader2, Code2 } from "lucide-react";
import { useIframeGuard } from "@/components/UnsavedChangesGuard";

export default function CotizadorIframe() {
  const iframeRef = useRef(null);
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  useIframeGuard();

  // Fetch iframe HTML code from settings
  useEffect(() => {
    const fetchCode = async () => {
      try {
        const res = await axios.get(`${API}/traveldesk/comercial_tabla`, { withCredentials: true });
        setCode(res.data.code || "");
      } catch (err) {
        console.error("Error loading cotizador code:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCode();
  }, []);

  // Send user to iframe when ready
  const sendUser = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { action: "authUser", user },
      "*"
    );
  }, [user]);

  // Send users list (admin/supervisor only)
  const sendUsersList = useCallback(async () => {
    if (user?.role !== "admin" && user?.role !== "supervisor") return;
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      iframeRef.current?.contentWindow?.postMessage(
        { action: "usersList", users: res.data },
        "*"
      );
    } catch (err) {
      console.error("[Backoffice PD] Error obteniendo usuarios:", err);
    }
  }, [user]);

  // Send clients list to iframe
  const sendClientesList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients`, { withCredentials: true });
      const clientes = Array.isArray(res.data) ? res.data : (res.data.clientes || res.data.results || []);
      iframeRef.current?.contentWindow?.postMessage(
        { action: "clientesList", clientes },
        "*"
      );
    } catch (err) {
      console.error("[Backoffice PD] Error obteniendo clientes:", err);
    }
  }, []);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = async (e) => {
      const data = e.data;
      if (!data || typeof data !== "object") return;

      switch (data.action) {
        case "requestAuthUser":
          sendUser();
          break;
        case "requestUsers":
          sendUsersList();
          break;
        case "requestClientes":
          sendClientesList();
          break;
        case "cotizadorReady":
          sendUser();
          sendClientesList();
          sendUsersList();
          break;
        case undefined:
          if (data.tipo === "cotizacion_pdtravel") {
            await saveCotizacion(data);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [sendUser, sendUsersList, sendClientesList]);

  // Save cotizacion to backend (new collection)
  const saveCotizacion = async (payload) => {
    try {
      await axios.post(`${API}/cotizaciones-tabla`, {
        proyecto: payload.proyecto,
        version: payload.version_nombre,
        cliente: payload.cliente,
        cliente_id: payload.cliente_id || null,
        destino: payload.destino,
        pasajeros: payload.pasajeros,
        moneda: payload.moneda,
        gran_total: payload.totales?.gran_total,
        agente_id: payload.agente?.id || null,
        agente_name: payload.agente?.name || null,
        fecha_cotizacion: payload.fecha_cotizacion,
        fecha_vigencia: payload.fecha_vigencia,
        payload_completo: payload,
        created_at: payload.timestamp,
      }, { withCredentials: true });
      console.log("[Backoffice PD] Cotización guardada:", payload.proyecto);
    } catch (err) {
      console.error("[Backoffice PD] Error guardando cotización:", err);
    }
  };

  // Load a saved cotizacion into the iframe
  const loadCotizacion = (snapshotFromDB) => {
    iframeRef.current?.contentWindow?.postMessage(
      { action: "loadCotizacion", snapshot: snapshotFromDB },
      "*"
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-[calc(100vh-100px)]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!code) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-[calc(100vh-100px)] text-center" data-testid="cotizador-iframe-empty">
        <Code2 className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Cotizador</h2>
        <p className="text-slate-500 max-w-md">
          No hay contenido configurado. El administrador puede agregar el código HTML desde Configuración → Opciones → Comercial.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-16px)]" data-testid="cotizador-iframe-page">
      <iframe
        ref={iframeRef}
        srcDoc={code}
        style={{ width: "100%", height: "100%", border: "none" }}
        title="Cotizador PD Travel"
        onLoad={sendUser}
      />
    </div>
  );
}
